import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessagePolling } from '../../hooks/useMessagePolling'; // ✅ Import polling hook
import { getLanguageDirection } from '../../i18n/config';
import { AILegalMessage } from '../../models/ai-legal-message';
import { setIsChatLoading } from '../../redux/slices/chatSlice';
import { cleanupStaleProgresses } from '../../redux/slices/legalQueryProgressSlice';
import { clearAllProgress } from '../../redux/slices/simpleLegalProgressSlice'; // ✅ Import simple progress cleanup
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { DBMessageThunks, MessageReducerActions, messageSelectByQuery } from '../../redux/thunks/DBServiceThunks';
import { restoreProgressFromBackup } from '../../redux/thunks/SimpleMQTTThunks'; // ✅ Import backup restore
import ChatHeader from './chats/ChatHeader';
import ChatInputContainer from './chats/ChatInputContainer';
import MessageList from './chats/MessageList';

/**
 * Main chat view component with premium UI
 */
const ChatView: React.FC = () => {
    const dispatch = useAppDispatch();
    const currentChatId = useAppSelector(state => state.ChatReducer.currentChatId);
    const currentChat = useAppSelector(state =>
        currentChatId ? state.DBChatReducer.entities[currentChatId] : null
    );
    const isChatLoading = useAppSelector(state => state.ChatReducer.isChatLoading);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { t, i18n } = useTranslation();
    const isRTL = getLanguageDirection(i18n.language) === 'rtl';

    // Lazy loading state
    const BATCH_SIZE = 15;
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Check if current chat is a document search
    const isDocumentSearchChat = currentChat?.type === 'documentSearch';

    // ✅ Get message entities and memoize filtering to prevent re-renders
    const messageEntities = useAppSelector(state => state.DBMessageReducer.entities);
    const messages = useMemo(() => {
        if (!currentChatId) return [];
        return Object.values(messageEntities).filter(
            (msg): msg is AILegalMessage => msg?.chatId === currentChatId
        );
    }, [currentChatId, messageEntities]);

    // ✅ Check if we need to start polling for incomplete messages
    const pollingConfig = React.useMemo(() => {

        if (!currentChatId || !messages.length) {
            return { shouldPoll: false, queryId: undefined };
        }

        // Get all messages for this chat, sorted by timestamp
        const chatMessages = messages
            .filter(msg => msg.chatId === currentChatId)
            .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());

        const latestMessage = chatMessages[0];
        if (!latestMessage) {
            return { shouldPoll: false, queryId: undefined };
        }

        // Check if message is incomplete
        const status = latestMessage.metadata?.status;
        const sender = latestMessage.sender;
        const queryId = latestMessage.metadata?.queryId;
        const isIncomplete = status !== 'completed' && status !== 'error';

        // Check if message is recent (less than 20 minutes old)
        const messageAge = Date.now() - new Date(latestMessage.timestamp || 0).getTime();
        const isRecent = messageAge < 20 * 60 * 1000; // 20 minutes

        // ✅ CRITICAL: Only poll for ASSISTANT messages with queryId (not user messages)
        const shouldPoll = sender === 'assistant' && queryId && isIncomplete && isRecent;


        return { shouldPoll, queryId };
    }, [currentChatId, messages]);

    // ✅ Use polling hook with queryId for precise message matching
    const { isPolling } = useMessagePolling(currentChatId, pollingConfig.shouldPoll, pollingConfig.queryId);

    // ✅ Cleanup effect: Remove stale progress when chat changes
    useEffect(() => {
        if (currentChatId) {
            // Clean up any stale progress when switching chats
            dispatch(clearAllProgress());
        }
    }, [currentChatId, dispatch]);

    // ✅ Auto-scroll to user's query when AI response completes
    const previousStatusRef = useRef<string | undefined>();
    useEffect(() => {
        if (!currentChatId || !messages.length) return;

        // Get latest assistant message
        const latestAssistantMessage = messages
            .filter(msg => msg.sender === 'assistant' && msg.chatId === currentChatId)
            .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())[0];

        if (!latestAssistantMessage) return;

        const currentStatus = latestAssistantMessage.metadata?.status;
        const previousStatus = previousStatusRef.current;

        // Detect transition from 'active' to 'completed'
        if (previousStatus === 'active' && currentStatus === 'completed') {
            // Find the user message that triggered this AI response (immediately before it)
            const sortedMessages = messages
                .filter(msg => msg.chatId === currentChatId)
                .sort((a, b) => new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime());

            const assistantIndex = sortedMessages.findIndex(msg => msg._id === latestAssistantMessage._id);
            const userMessage = assistantIndex > 0 ? sortedMessages[assistantIndex - 1] : null;

            // Scroll to user's query message (shows question at top, answer below)
            if (userMessage?._id && userMessage.sender === 'user') {
                setTimeout(() => {
                    const messageElement = document.querySelector(`[data-message-id="${userMessage._id}"]`);
                    if (messageElement) {
                        messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            }
        }

        // Update ref for next comparison
        previousStatusRef.current = currentStatus;
    }, [currentChatId, messages]);

    const getChatMessagesFromDB = useCallback(async () => {
        await dispatch(setIsChatLoading(true));
        try {
            // Fetch first batch of messages (15 most recent)
            const messagesResult = await dispatch(DBMessageThunks.find({
                chatId: currentChatId,
                $limit: BATCH_SIZE,
                $sort: { timestamp: -1 } // Newest first
            }));

            // Check if there are more messages to load
            const fetchedMessages = messagesResult.payload || [];
            setHasMoreMessages(fetchedMessages.length === BATCH_SIZE);

            // ✅ Clear all progress on chat load - MQTT will restore active ones automatically
            dispatch(clearAllProgress());
            dispatch(cleanupStaleProgresses()); // Clean up old system too

            // ✅ After clearing, check if we need to restore progress for incomplete messages
            // Use the messages that were just fetched
            const chatMessages = fetchedMessages
                .filter(msg => msg.chatId === currentChatId)
                .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());

            const latestMessage = chatMessages[0];
            if (latestMessage?.sender === 'assistant' &&
                latestMessage?.metadata?.status !== 'completed' &&
                latestMessage?.metadata?.status !== 'error' &&
                latestMessage?.metadata?.queryId &&
                latestMessage._id) {

                const messageAge = Date.now() - new Date(latestMessage.timestamp || 0).getTime();
                const isRecent = messageAge < 20 * 60 * 1000; // 20 minutes

                if (isRecent) {
                    // ✅ Restore progress from database backup (includes lastProgressUpdate and lastFeedUpdate)
                    try {
                        await dispatch(restoreProgressFromBackup({ messageId: latestMessage._id }));
                        console.log(`🔄 [ChatView] Successfully restored progress for incomplete message: ${latestMessage._id}`);
                    } catch (error) {
                        console.error(`❌ [ChatView] Failed to restore progress:`, error);
                        // Don't throw - continue anyway
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
        await dispatch(setIsChatLoading(false));

        // Scroll to bottom after messages are loaded - use direct scrollTop
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }
        }, 100);
    }, [currentChatId, dispatch]);

    // Load more messages (lazy loading)
    const loadMoreMessages = useCallback(async () => {
        if (isLoadingMore || !hasMoreMessages || !currentChatId || !scrollContainerRef.current) return;

        // Save current scroll position before loading more
        const scrollContainer = scrollContainerRef.current;
        const previousScrollHeight = scrollContainer.scrollHeight;
        const previousScrollTop = scrollContainer.scrollTop;

        setIsLoadingMore(true);
        try {
            // Get current messages count for this chat from messages array
            const currentMessagesCount = messages.filter(msg => msg.chatId === currentChatId).length;

            const result = await dispatch(DBMessageThunks.find({
                chatId: currentChatId,
                $limit: BATCH_SIZE,
                $skip: currentMessagesCount,
                $sort: { timestamp: -1 }
            }));

            const newMessages = result.payload || [];
            setHasMoreMessages(newMessages.length === BATCH_SIZE);

            // Restore scroll position after new messages are added at the top
            setTimeout(() => {
                if (scrollContainer) {
                    const newScrollHeight = scrollContainer.scrollHeight;
                    const heightDifference = newScrollHeight - previousScrollHeight;
                    scrollContainer.scrollTop = previousScrollTop + heightDifference;
                }
            }, 0);
        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [isLoadingMore, hasMoreMessages, currentChatId, messages, dispatch]);

    // Removed - scrolling is now handled in getChatMessagesFromDB after fetch completes

    // Load messages when the selected chat changes or on page refresh
    useEffect(() => {
        if (currentChatId) {
            // Clear previous chat messages from state
            dispatch(MessageReducerActions.removeAll());
            // Reset pagination state
            setHasMoreMessages(true);
            setIsLoadingMore(false);
            // Load new chat messages
            getChatMessagesFromDB();
        }
    }, [currentChatId, dispatch, getChatMessagesFromDB]);

    // Also fetch messages on page load/refresh
    useEffect(() => {
        if (currentChatId) {
            getChatMessagesFromDB();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // IntersectionObserver to trigger lazy loading when 2nd message is visible
    useEffect(() => {
        let observer: IntersectionObserver | null = null;

        // Wait for next frame to ensure DOM is rendered
        const timeoutId = setTimeout(() => {
            const triggerElement = loadMoreTriggerRef.current;

            if (!triggerElement || !hasMoreMessages || isLoadingMore) return;

            observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        loadMoreMessages();
                    }
                },
                {
                    root: scrollContainerRef.current,
                    threshold: 0.1
                }
            );

            observer.observe(triggerElement);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            if (observer) {
                observer.disconnect();
            }
        };
    }, [hasMoreMessages, isLoadingMore, messages.length, loadMoreMessages]);

    return (
        <div className="flex flex-col h-full w-full bg-background/50 relative overflow-hidden">
            {/* Chat Header */}
            <ChatHeader />

            {/* Main Chat Content Area */}
            <div className="flex-1 overflow-hidden flex flex-col relative">
                {/* Message List with Scroll */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
                    <MessageList
                        loadMoreTriggerRef={loadMoreTriggerRef}
                        hasMoreMessages={hasMoreMessages}
                        isLoadingMore={isLoadingMore}
                    />
                </div>

                {/* Input Container - Hidden for document search chats */}
                {!isDocumentSearchChat && <ChatInputContainer />}
            </div>

            {/* Subtle background gradient */}
            <div className="absolute inset-0 pointer-events-none z-0 opacity-60"
                style={{
                    background: 'radial-gradient(circle at 10% 10%, rgba(160,122,44,0.03) 0%, transparent 30%), radial-gradient(circle at 90% 90%, rgba(160,122,44,0.03) 0%, transparent 40%)'
                }}
            />
        </div>
    );
};

export default ChatView;
